// ForgeAssignment — Stages 27–30: operational readiness and first-duty assignment.
// Covers: crew assignment, fit test, gear issue, first duty.

import { useMemo } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useRecruitTraining } from "../../hooks/useForge";
import type { RecruitTrainingRow } from "../../hooks/useForge";
import {
  useFitTestsG1,
  useFitTestsAdvantage,
  useRoster,
  useGearInventory,
} from "../../hooks/useOps";
import type {
  FitTestG1Row,
  FitTestAdvantageRow,
  RosterRow,
} from "../../hooks/useOps";
import { useApplicants } from "../../hooks/useDashboard";
import type { ApplicantRow } from "../../hooks/useDashboard";
import KPICard from "../../components/dashboard/KPICard";
import { TruncationNoticeGroup } from "../../components/shared/TruncationNotice";
import ChartCard from "../../components/dashboard/ChartCard";
import StatusBadge from "../../components/dashboard/StatusBadge";
import { LiveIndicator } from "../../components/shared/LiveIndicator";
import { useTheme } from "../../hooks/useTheme";

// ── Constants ──────────────────────────────────────────────────────────────────

const DCVFD_ACCENT = "#0E403A";
const STATIONS = ["10", "13", "18", "20"];

// Days target from "released" to "first duty"
const TARGET_DAYS_TO_DUTY = 14;

// ── Helpers ───────────────────────────────────────────────────────────────────

function asString(v: unknown, fallback = ""): string {
  if (typeof v === "string") return v;
  return fallback;
}

function recruitName(row: RecruitTrainingRow): string {
  const m = asString((row as Record<string, unknown>)["member"]);
  return m || asString(row.airtable_id) || "Unknown";
}

function isReleased(row: RecruitTrainingRow): boolean {
  const s = asString(row.status).toLowerCase();
  return s === "released for assignment";
}

function isRunning(row: RecruitTrainingRow): boolean {
  const s = asString(row.status).toLowerCase();
  return s === "running" || s === "active" || s === "operational";
}

function daysSinceReleased(row: RecruitTrainingRow): number {
  const releaseDate = asString(
    (row as Record<string, unknown>)["release_date"] ??
      (row as Record<string, unknown>)["released_date"] ??
      (row as Record<string, unknown>)["date_released"],
  );
  if (!releaseDate) {
    // Fall back: use days in training as proxy
    return 0;
  }
  const d = new Date(releaseDate.replace(/"/g, ""));
  if (isNaN(d.getTime())) return 0;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

/** Normalize member name for cross-dataset join */
/**
 * Set of normalized member names with a recorded fit test, across BOTH feeds.
 *
 * The two tables DISAGREE on the member column: dash_fit_test_g1 spells it
 * `Members` (PLURAL), dash_fit_test_advantage spells it `Member`. This was one
 * read of `member` over the union of the two, so all 163 live G1 records
 * contributed nothing and a member whose only fit test was a G1 test counted
 * as untested. `FitTestG1Row` carried a `[key: string]: unknown` index
 * signature, so reading the column that does not exist compiled clean.
 */
export function buildFitTestedNames(
  g1: FitTestG1Row[],
  advantage: FitTestAdvantageRow[],
): Set<string> {
  const names = new Set<string>();
  for (const ft of g1) {
    if (ft.members) names.add(normalizeName(ft.members));
  }
  for (const ft of advantage) {
    if (ft.member) names.add(normalizeName(ft.member));
  }
  return names;
}

function normalizeName(name: string | null | undefined): string {
  if (!name) return "";
  return name.toLowerCase().trim().replace(/\s+/g, " ");
}

// RefreshDot removed — using shared LiveIndicator component

// ── Pending Onboarding Table ──────────────────────────────────────────────────

/** BOD-approved applicants who haven't yet appeared in recruit training */
function PendingOnboardingTable({
  applicants,
  trainingNames,
  isLoading,
}: {
  applicants: ApplicantRow[];
  trainingNames: Set<string>;
  isLoading: boolean;
}) {
  const pending = useMemo(() => {
    return applicants.filter((a) => {
      const bodVote = asString(
        (a as Record<string, unknown>)["bod_vote"] ??
          (a as Record<string, unknown>)["BOD_Vote"] ??
          "",
      );
      const result = asString(
        (a as Record<string, unknown>)["application_result"] ??
          (a as Record<string, unknown>)["Application_Result"] ??
          "",
      );
      if (!bodVote) return false; // no BOD vote date
      if (
        !result.toLowerCase().includes("bod approved") &&
        !result.toLowerCase().includes("board approved") &&
        !result.toLowerCase().includes("approved")
      ) {
        // Also include stage = 8-Complete / Complete as an indicator of BOD approval
        const stage = asString(a.pipeline_stage).toLowerCase();
        if (
          !stage.includes("8") &&
          !stage.includes("complete") &&
          !stage.includes("approved")
        ) {
          return false;
        }
      }
      // Check if already in recruit training (by name match)
      const first = asString(a.first_name);
      const last = asString(a.last_name);
      const nameId = asString(a.applicant_name_id);
      const fullName = first && last ? `${first} ${last}`.trim() : nameId;
      if (!fullName) return false;
      return !trainingNames.has(normalizeName(fullName));
    });
  }, [applicants, trainingNames]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skeleton h-10 rounded-xl" />
        ))}
      </div>
    );
  }

  if (pending.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-status-green"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <p className="text-sm font-medium text-status-green">
          No pending onboarding
        </p>
        <p className="text-xs text-theme-muted opacity-70">
          All BOD-approved members are in recruit training
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider">
              Name
            </th>
            <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
              BOD Vote
            </th>
            <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
              Days Since BOD
            </th>
            <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {pending.map((a, i) => {
            const first = asString(a.first_name);
            const last = asString(a.last_name);
            const name =
              first && last
                ? `${first} ${last}`.trim()
                : asString(a.applicant_name_id) || "Unknown";
            const bodVote = asString(
              (a as Record<string, unknown>)["bod_vote"] ??
                (a as Record<string, unknown>)["BOD_Vote"] ??
                "",
            ).replace(/"/g, "");
            let daysSinceBod = 0;
            if (bodVote) {
              const d = new Date(bodVote);
              if (!isNaN(d.getTime())) {
                daysSinceBod = Math.floor(
                  (Date.now() - d.getTime()) / 86400000,
                );
              }
            }
            const isOverdue = daysSinceBod > 30;
            return (
              <tr
                key={`${name}-${i}`}
                className="hover:bg-theme-overlay/50 transition-colors"
              >
                <td className="px-3 py-2 font-medium text-theme-primary">
                  {name}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-theme-secondary">
                  {bodVote ? bodVote.slice(0, 10) : "—"}
                </td>
                <td
                  className={`px-3 py-2 text-right tabular-nums font-semibold ${isOverdue ? "text-status-red" : "text-theme-secondary"}`}
                >
                  {daysSinceBod > 0 ? `${daysSinceBod}d` : "—"}
                </td>
                <td className="px-3 py-2 text-theme-muted italic">
                  Not yet in recruit training
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-xs text-theme-muted mt-3 px-1">
        These {pending.length} member{pending.length !== 1 ? "s" : ""} have a
        BOD Vote date and are BOD-approved but have no record in recruit
        training. Follow up to confirm onboarding is scheduled.
      </p>
    </div>
  );
}

// ── Readiness queue item ──────────────────────────────────────────────────────

interface ReadinessItem {
  name: string;
  daysSinceReleased: number;
  hasAssignment: boolean;
  hasFitTest: boolean;
  hasGear: boolean;
  isOnDuty: boolean;
}

function blockingStep(item: ReadinessItem): string {
  if (!item.hasAssignment) return "No crew assignment";
  if (!item.hasFitTest) return "Fit test not completed";
  if (!item.hasGear) return "Gear not issued";
  if (!item.isOnDuty) return "Not yet active — contact station officer";
  return "All gates passed";
}

function ReadinessQueueTable({
  items,
  isLoading,
}: {
  items: ReadinessItem[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="skeleton h-10 rounded-xl" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-status-green"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        <p className="text-sm font-medium text-status-green">
          No recruits pending assignment
        </p>
        <p className="text-xs text-theme-muted opacity-70">
          All released recruits have been activated
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider">
              Name
            </th>
            <th className="px-3 py-2 text-right text-theme-muted font-semibold uppercase tracking-wider">
              Days Waiting
            </th>
            <th className="px-3 py-2 text-center text-theme-muted font-semibold uppercase tracking-wider">
              Assignment
            </th>
            <th className="px-3 py-2 text-center text-theme-muted font-semibold uppercase tracking-wider">
              Fit Test
            </th>
            <th className="px-3 py-2 text-center text-theme-muted font-semibold uppercase tracking-wider">
              Gear
            </th>
            <th className="px-3 py-2 text-left text-theme-muted font-semibold uppercase tracking-wider">
              Blocking Step
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isLong = item.daysSinceReleased > TARGET_DAYS_TO_DUTY;
            return (
              <tr
                key={item.name}
                className="hover:bg-theme-overlay/50 transition-colors"
              >
                <td className="px-3 py-2 font-medium text-theme-primary">
                  {item.name}
                </td>
                <td
                  className={`px-3 py-2 text-right tabular-nums font-semibold ${isLong ? "text-status-red" : "text-theme-secondary"}`}
                >
                  {item.daysSinceReleased > 0
                    ? `${item.daysSinceReleased}d`
                    : "—"}
                </td>
                <td className="px-3 py-2 text-center">
                  {item.hasAssignment ? (
                    <span className="text-status-green">✓</span>
                  ) : (
                    <span className="text-status-red">✗</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {item.hasFitTest ? (
                    <span className="text-status-green">✓</span>
                  ) : (
                    <span className="text-status-amber">✗</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {item.hasGear ? (
                    <span className="text-status-green">✓</span>
                  ) : (
                    <span className="text-status-amber">✗</span>
                  )}
                </td>
                <td className="px-3 py-2 text-theme-muted italic">
                  {blockingStep(item)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Days to First Duty Histogram ──────────────────────────────────────────────

interface DutyHistoBucket {
  label: string;
  count: number;
  minDays: number;
  maxDays: number;
}

const DUTY_HISTO_BUCKETS: DutyHistoBucket[] = [
  { label: "0–7d", count: 0, minDays: 0, maxDays: 7 },
  { label: "7–14d", count: 0, minDays: 7, maxDays: 14 },
  { label: "14–21d", count: 0, minDays: 14, maxDays: 21 },
  { label: "21–30d", count: 0, minDays: 21, maxDays: 30 },
  { label: "30d+", count: 0, minDays: 30, maxDays: Infinity },
];

interface DutyTooltipPayload {
  value: number;
  payload: DutyHistoBucket;
}

function DutyHistoTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: DutyTooltipPayload[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[140px]">
      <p className="font-semibold text-theme-primary mb-1">{label}</p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">Recruits</span>
        <span className="font-bold tabular-nums">{payload[0].value}</span>
      </div>
      {payload[0].payload.minDays >= TARGET_DAYS_TO_DUTY &&
        payload[0].value > 0 && (
          <p className="text-status-amber mt-1">
            Over {TARGET_DAYS_TO_DUTY}d target
          </p>
        )}
    </div>
  );
}

function DaysToFirstDutyHistogram({
  items,
  isLoading,
}: {
  items: ReadinessItem[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  const histogramData = useMemo(() => {
    const buckets = DUTY_HISTO_BUCKETS.map((b) => ({ ...b, count: 0 }));
    for (const item of items) {
      if (item.daysSinceReleased <= 0) continue;
      for (const bucket of buckets) {
        if (
          item.daysSinceReleased > bucket.minDays &&
          item.daysSinceReleased <= bucket.maxDays
        ) {
          bucket.count += 1;
          break;
        }
      }
    }
    return buckets;
  }, [items]);

  const hasData = histogramData.some((b) => b.count > 0);

  if (isLoading) {
    return <div className="skeleton h-44 rounded-xl" />;
  }

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">
          No days-to-duty data available
        </p>
        <p className="text-xs text-theme-muted opacity-60">
          Requires release date data on recruit records
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-theme-muted">
        Target: first duty within {TARGET_DAYS_TO_DUTY} days of release
      </p>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart
          data={histogramData}
          margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
        >
          <CartesianGrid
            vertical={false}
            stroke={gridColor}
            strokeDasharray="3 3"
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: axisColor }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: axisColor }}
            tickLine={false}
            axisLine={false}
            width={32}
            allowDecimals={false}
          />
          <Tooltip
            content={<DutyHistoTooltip />}
            cursor={{
              fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
            }}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={60}>
            {histogramData.map((entry, i) => {
              const color =
                entry.maxDays <= TARGET_DAYS_TO_DUTY ? "#16a34a" : "#f59e0b";
              return <Cell key={`cell-${i}`} fill={color} fillOpacity={0.85} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Station Distribution ──────────────────────────────────────────────────────
//
// WHAT WAS WRONG. This chart read `asString(row.station)` off every roster row
// and bucketed anything unrecognized into "Other". `dash_roster` has no
// `station` column and never has — `RosterRow` declared one behind an index
// signature, so it compiled — and the chart therefore reported, over the 305
// live members: Station 10 = 0, Station 13 = 0, Station 18 = 0, Station 20 = 0,
// Other = 305. Same failure as the community-events chart that put all 92
// events in "Other": it does not look broken, it looks like a department whose
// members are not at any station.
//
// WHY IT IS NOT REPOINTED AT ANOTHER COLUMN. Verified against remote D1
// (2026-08-27), every candidate on the table is an unresolved Airtable link:
//
//   Location_from_Unit   179/305 NULL; the other 126 are 3 distinct record IDs
//   Battalion            5 distinct record IDs
//   Unit                 10 distinct record IDs
//   Assignment           free text, "Battalion 3 - E513 (DC-24) - LFF" — a
//                        battalion and an apparatus, not a station
//
// No human-readable station value exists anywhere on the feed. Pointing the
// chart at any of those would print `recYOVRz1ZldJDlRL` as a station name,
// which is worse than the zeros, not better. There is no in-table fix. So the
// chart stops asserting a distribution and says what it does not know.
//
// WHY THIS IS A SCAN AND NOT A NEW COLUMN NAME. A hard-coded `row.station` is
// what produced the zeros; a hard-coded replacement would produce the next
// set. `findStationBearingKey` asks the WIRE whether anything on it names a
// station, so if the sync ever resolves those links this chart comes back on
// its own and nobody has to remember to edit this file.

interface StationCount {
  station: string;
  count: number;
  newRecruits: number;
}

/**
 * True if `value` is, in its entirety, a designator for a station DCVFD runs.
 *
 * Whole-value match on purpose. A substring match would find "10" inside the
 * member ID in `"... (102499) Battalion 3 ..."` and happily build a station
 * chart out of payroll numbers.
 */
function namesAStation(value: unknown): string | null {
  const raw =
    typeof value === "string"
      ? value.trim()
      : typeof value === "number"
        ? String(value)
        : "";
  if (!raw) return null;
  const m = /^(?:fs|sta|station)?[\s\-_]*(\d{1,2})$/i.exec(raw);
  if (!m) return null;
  return STATIONS.includes(m[1]) ? m[1] : null;
}

/**
 * Find the single wire field that carries station assignments, or null.
 *
 * A field qualifies only if EVERY non-empty value on it names a station. That
 * is deliberately strict: a column where some values are stations and some are
 * record IDs cannot be bucketed without inventing an "Other" that means two
 * different things. Against the live feed nothing qualifies, which is the
 * correct answer today.
 *
 * Returns the key plus its coverage, because a field present on 126 of 305
 * rows still cannot describe the roster and the panel has to say so.
 */
export function findStationBearingKey(roster: RosterRow[]): {
  key: string;
  covered: number;
} | null {
  const keys = new Set<string>();
  for (const row of roster) {
    for (const k of Object.keys(row)) keys.add(k);
  }
  let best: { key: string; covered: number } | null = null;
  for (const key of keys) {
    let covered = 0;
    let disqualified = false;
    for (const row of roster) {
      const v = (row as Record<string, unknown>)[key];
      if (v === null || v === undefined || v === "") continue;
      if (namesAStation(v) === null) {
        disqualified = true;
        break;
      }
      covered += 1;
    }
    if (disqualified || covered === 0) continue;
    if (best === null || covered > best.covered) best = { key, covered };
  }
  return best;
}

export type StationDistributionState =
  | {
      kind: "loading";
    }
  | {
      kind: "no-roster";
    }
  | {
      kind: "unknown";
      rosterRows: number;
      reason: string;
      footnote: string;
    }
  | {
      kind: "data";
      rows: StationCount[];
      covered: number;
      rosterRows: number;
    };

/**
 * Resolve what the station card should render.
 *
 * `unknown` is a first-class outcome here, not an error state. It reports the
 * roster size so the reader can see the feed loaded fine — the members are
 * there, the station is not.
 */
export function stationDistributionState(
  roster: RosterRow[],
  releasedNames: Set<string>,
  isLoading: boolean,
): StationDistributionState {
  if (isLoading) return { kind: "loading" };
  if (roster.length === 0) return { kind: "no-roster" };

  const source = findStationBearingKey(roster);
  if (source === null) {
    return {
      kind: "unknown",
      rosterRows: roster.length,
      reason:
        `Station is not on the roster feed. All ${roster.length} members ` +
        "loaded, but nothing on the record names a station - the location, " +
        "battalion and unit fields are unresolved Airtable record IDs, and " +
        "the assignment text carries a battalion and an apparatus instead.",
      footnote:
        "Showing every member under one bucket would read as a real " +
        "distribution, so no distribution is shown. This card returns by " +
        "itself once the sync resolves those links.",
    };
  }

  const counts = new Map<string, { total: number; newRecruits: number }>();
  for (const station of STATIONS) {
    counts.set(station, { total: 0, newRecruits: 0 });
  }
  let covered = 0;
  for (const row of roster) {
    const station = namesAStation((row as Record<string, unknown>)[source.key]);
    if (station === null) continue;
    covered += 1;
    const entry = counts.get(station) ?? { total: 0, newRecruits: 0 };
    entry.total += 1;
    if (releasedNames.has(normalizeName(row.member))) entry.newRecruits += 1;
    counts.set(station, entry);
  }

  return {
    kind: "data",
    covered,
    rosterRows: roster.length,
    rows: STATIONS.map((st) => ({
      station: `Station ${st}`,
      count: counts.get(st)?.total ?? 0,
      newRecruits: counts.get(st)?.newRecruits ?? 0,
    })).filter((r) => r.count > 0),
  };
}

export function StationDistribution({
  roster,
  releasedNames,
  isLoading,
}: {
  roster: RosterRow[];
  releasedNames: Set<string>;
  isLoading: boolean;
}) {
  const state = useMemo(
    () => stationDistributionState(roster, releasedNames, isLoading),
    [roster, releasedNames, isLoading],
  );

  if (state.kind === "loading") {
    return <div className="skeleton h-44 rounded-xl" />;
  }

  if (state.kind === "no-roster") {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-2">
        <p className="text-sm text-theme-muted">No roster data available</p>
        <p className="text-xs text-theme-muted opacity-60">
          The roster feed returned no rows.
        </p>
      </div>
    );
  }

  if (state.kind === "unknown") {
    return (
      <div className="py-6 px-2 flex flex-col items-center gap-2 text-center">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-status-amber"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
        </svg>
        <p className="text-sm font-semibold text-theme-primary">
          Unknown - station is not on this feed
        </p>
        <p className="text-xs text-theme-muted max-w-md">{state.reason}</p>
        <p className="text-xs text-theme-muted opacity-70 max-w-md">
          {state.footnote}
        </p>
      </div>
    );
  }

  const maxCount = state.rows.reduce((m, s) => Math.max(m, s.count), 0);

  return (
    <div className="space-y-3">
      {state.covered < state.rosterRows && (
        <p className="text-xs text-status-amber">
          {state.rosterRows - state.covered} of {state.rosterRows} members carry
          no station and are not counted below.
        </p>
      )}
      {state.rows.map((s) => {
        const pct = maxCount > 0 ? (s.count / maxCount) * 100 : 0;
        return (
          <div key={s.station} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-theme-primary">
                {s.station}
              </span>
              <div className="flex items-center gap-3">
                {s.newRecruits > 0 && (
                  <span className="text-status-green font-semibold">
                    +{s.newRecruits} new
                  </span>
                )}
                <span className="tabular-nums text-theme-secondary font-semibold">
                  {s.count}
                </span>
              </div>
            </div>
            <div className="h-2 rounded-full bg-theme-overlay overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${pct}%`, backgroundColor: DCVFD_ACCENT }}
              />
            </div>
          </div>
        );
      })}
      <p className="text-xs text-theme-muted">
        Green &quot;+N new&quot; indicates recently released recruits assigned
        to that station.
      </p>
    </div>
  );
}

// ── Readiness Gap Cards ───────────────────────────────────────────────────────

interface ReadinessGapProps {
  released: ReadinessItem[];
  isLoading: boolean;
}

function ReadinessGap({ released, isLoading }: ReadinessGapProps) {
  const notAssigned = released.filter((r) => !r.hasAssignment).length;
  const assignedNoFitTest = released.filter(
    (r) => r.hasAssignment && !r.hasFitTest,
  ).length;
  const fitNoGear = released.filter((r) => r.hasFitTest && !r.hasGear).length;
  const allGatesNotDuty = released.filter(
    (r) => r.hasAssignment && r.hasFitTest && r.hasGear && !r.isOnDuty,
  ).length;

  const gaps = [
    {
      label: "Released, Not Assigned",
      count: notAssigned,
      description: "Need crew assignment",
      color: notAssigned > 0 ? "text-status-red" : "text-status-green",
      action: "Contact battalion chief to assign to a crew at a station.",
    },
    {
      label: "Assigned, No Fit Test",
      count: assignedNoFitTest,
      description: "Fit test not on record",
      color: assignedNoFitTest > 0 ? "text-status-amber" : "text-status-green",
      action: "Schedule SCBA G1 and Advantage respirator fit tests.",
    },
    {
      label: "Fit Tested, No Gear",
      count: fitNoGear,
      description: "PPE not yet issued",
      color: fitNoGear > 0 ? "text-status-amber" : "text-status-green",
      action: "Contact equipment officer to process gear issue.",
    },
    {
      label: "All Gates Passed, Not On Duty",
      count: allGatesNotDuty,
      description: "Mystery losses — investigate",
      color: allGatesNotDuty > 0 ? "text-status-red" : "text-status-green",
      action:
        "These recruits have completed all prerequisites but are not yet active. Direct follow-up required.",
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton h-28 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {gaps.map((gap) => (
          <div
            key={gap.label}
            className="glass rounded-xl p-4 flex flex-col gap-2"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-semibold text-theme-muted uppercase tracking-wider leading-snug">
                {gap.label}
              </p>
              <span
                className={`text-3xl font-bold tabular-nums shrink-0 ${gap.color}`}
              >
                {gap.count}
              </span>
            </div>
            <p className="text-xs text-theme-muted">{gap.description}</p>
            {gap.count > 0 && (
              <p className="text-xs text-theme-secondary font-medium border-t border-theme-subtle pt-2 mt-auto">
                Action: {gap.action}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ForgeAssignment() {
  const INTERVAL = 120_000;

  const {
    data: trainingData,
    isLoading: trainingLoading,
    lastUpdatedAt,
  } = useRecruitTraining(INTERVAL);
  const { data: fitTestG1Data, isLoading: fitG1Loading } =
    useFitTestsG1(INTERVAL);
  const { data: fitTestAdvData, isLoading: fitAdvLoading } =
    useFitTestsAdvantage(INTERVAL);
  const { data: rosterData, isLoading: rosterLoading } = useRoster(
    undefined,
    INTERVAL,
  );
  const { data: gearData, isLoading: gearLoading } = useGearInventory(INTERVAL);
  const { data: applicantsData, isLoading: applicantsLoading } = useApplicants(
    { limit: 5000 },
    INTERVAL,
  );

  const allRecruits = trainingData?.data ?? [];
  const fitTestsG1 = fitTestG1Data?.data ?? [];
  const fitTestsAdv = fitTestAdvData?.data ?? [];
  const roster = rosterData?.data ?? [];
  const gear = gearData?.data ?? [];
  const allApplicants = applicantsData?.applicants ?? [];

  const isLoading =
    trainingLoading ||
    fitG1Loading ||
    fitAdvLoading ||
    rosterLoading ||
    gearLoading;

  // ── Build sets for quick cross-dataset lookup ──────────────────────────────
  // The two fit-test tables DISAGREE on the member column: dash_fit_test_g1
  // spells it `Members` (plural), dash_fit_test_advantage spells it `Member`.
  // Reading `member` off the union silently dropped all 163 G1 records from
  // this set, so a member with only a G1 fit test counted as untested.
  const fitTestedNames = useMemo<Set<string>>(
    () => buildFitTestedNames(fitTestsG1, fitTestsAdv),
    [fitTestsG1, fitTestsAdv],
  );

  const assignedNames = useMemo<Set<string>>(() => {
    const names = new Set<string>();
    for (const row of roster) {
      if (row.member) names.add(normalizeName(row.member));
    }
    return names;
  }, [roster]);

  const gearedNames = useMemo<Set<string>>(() => {
    const names = new Set<string>();
    for (const row of gear) {
      const member = asString(row.member);
      if (member) names.add(normalizeName(member));
    }
    return names;
  }, [gear]);

  // ── Derived recruits ──────────────────────────────────────────────────────
  const releasedRecruits = useMemo(
    () => allRecruits.filter(isReleased),
    [allRecruits],
  );
  const runningRecruits = useMemo(
    () => allRecruits.filter(isRunning),
    [allRecruits],
  );

  // ── Training names for pending-onboarding cross-reference ─────────────────
  const trainingNames = useMemo<Set<string>>(() => {
    const names = new Set<string>();
    for (const r of allRecruits) {
      names.add(normalizeName(recruitName(r)));
    }
    return names;
  }, [allRecruits]);

  const releasedNames = useMemo<Set<string>>(() => {
    return new Set(releasedRecruits.map((r) => normalizeName(recruitName(r))));
  }, [releasedRecruits]);

  // ── KPI values ─────────────────────────────────────────────────────────────
  const releasedCount = releasedRecruits.length;
  const crewAssigned = releasedRecruits.filter((r) =>
    assignedNames.has(normalizeName(recruitName(r))),
  ).length;
  const fitTested = releasedRecruits.filter((r) =>
    fitTestedNames.has(normalizeName(recruitName(r))),
  ).length;
  const onDutyCount = runningRecruits.length;

  // ── Readiness queue items ──────────────────────────────────────────────────
  const readinessItems = useMemo<ReadinessItem[]>(() => {
    return releasedRecruits
      .map((row) => {
        const name = recruitName(row);
        const norm = normalizeName(name);
        const hasAssignment = assignedNames.has(norm);
        const hasFitTest = fitTestedNames.has(norm);
        const hasGear = gearedNames.has(norm);
        const isOnDuty = isRunning(row);
        return {
          name,
          daysSinceReleased: daysSinceReleased(row),
          hasAssignment,
          hasFitTest,
          hasGear,
          isOnDuty,
        };
      })
      .filter((r) => !r.isOnDuty) // Only show those not yet fully active
      .sort((a, b) => b.daysSinceReleased - a.daysSinceReleased);
  }, [releasedRecruits, assignedNames, fitTestedNames, gearedNames]);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">Assignment</h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Stages 27–30 — Operational readiness: crew assignment, fit test,
            gear issue, and first-duty activation.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <LiveIndicator lastUpdatedAt={lastUpdatedAt} />
        </div>
      </div>

      {/* ── Row-completeness notice ───────────────────────────────────
          This page decides whether a recruit HAS a crew assignment, a fit test,
          and issued gear by looking for their name in the roster, fit-test and
          gear tables. A truncated read of any of those turns into a false
          "missing" against a real member, so a partial load has to be visible
          before anyone works this queue. */}
      <TruncationNoticeGroup
        sources={[
          { label: "Roster", response: rosterData },
          { label: "G1 fit tests", response: fitTestG1Data },
          { label: "Advantage fit tests", response: fitTestAdvData },
          { label: "Gear inventory", response: gearData },
        ]}
      />

      {/* ── KPI Row ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Readiness Pipeline
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPICard
            label="Released for Assignment"
            value={trainingLoading ? "—" : releasedCount}
            subtitle="completed all training modules"
            accent="neutral"
            isLoading={trainingLoading}
          />

          <KPICard
            label="Crew Assigned"
            value={isLoading ? "—" : crewAssigned}
            subtitle={`of ${releasedCount} released`}
            accent={
              releasedCount === 0
                ? "neutral"
                : crewAssigned === releasedCount
                  ? "green"
                  : crewAssigned >= releasedCount * 0.7
                    ? "amber"
                    : "red"
            }
            isLoading={isLoading}
            trend={
              !isLoading && releasedCount > 0
                ? {
                    direction: crewAssigned === releasedCount ? "up" : "down",
                    label: `${releasedCount - crewAssigned} unassigned`,
                    positiveIsGood: false,
                  }
                : undefined
            }
          />

          <KPICard
            label="Fit Tested"
            value={isLoading ? "—" : fitTested}
            subtitle="SCBA G1 or Advantage on record"
            accent={
              releasedCount === 0
                ? "neutral"
                : fitTested === releasedCount
                  ? "green"
                  : fitTested >= releasedCount * 0.7
                    ? "amber"
                    : "red"
            }
            isLoading={isLoading}
            trend={
              !isLoading && releasedCount > 0
                ? {
                    direction: fitTested === releasedCount ? "up" : "down",
                    label: `${releasedCount - fitTested} not fit tested`,
                    positiveIsGood: false,
                  }
                : undefined
            }
          />

          <KPICard
            label="On Duty"
            value={trainingLoading ? "—" : onDutyCount}
            subtitle="Running / operational status"
            accent={onDutyCount > 0 ? "green" : "neutral"}
            isLoading={trainingLoading}
            trend={
              !trainingLoading && releasedCount > 0
                ? {
                    direction: onDutyCount > 0 ? "up" : "down",
                    label:
                      onDutyCount > 0
                        ? `${onDutyCount} active`
                        : "None active yet",
                    positiveIsGood: true,
                  }
                : undefined
            }
          />
        </div>
      </section>

      {/* ── Section 1: Readiness Gap Analysis ────────────────────────── */}
      <section>
        <ChartCard
          title="Readiness Gap Analysis"
          subtitle="Released recruits who have not yet completed each prerequisite gate to active duty"
          minHeight={240}
        >
          <ReadinessGap released={readinessItems} isLoading={isLoading} />
        </ChartCard>
      </section>

      {/* ── Sections 2 & 3: Histogram + Station Distribution ─────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Days to First Duty Histogram */}
        <ChartCard
          title="Days to First Duty"
          subtitle={`Time since release — target: <${TARGET_DAYS_TO_DUTY} days`}
          minHeight={220}
        >
          <DaysToFirstDutyHistogram
            items={readinessItems}
            isLoading={isLoading}
          />
        </ChartCard>

        {/* Station Assignment Distribution */}
        <ChartCard
          title="Station Assignment Distribution"
          subtitle="Roster size per station, when the roster feed carries one"
          minHeight={220}
        >
          <StationDistribution
            roster={roster}
            releasedNames={releasedNames}
            isLoading={rosterLoading}
          />
        </ChartCard>
      </section>

      {/* ── Section 4: Readiness Queue ────────────────────────────────── */}
      <section>
        <ChartCard
          title="Readiness Queue"
          subtitle="Released recruits not yet on duty — sorted by days waiting, longest first"
          headerRight={
            !isLoading && readinessItems.length > 0 ? (
              <StatusBadge
                label={`${readinessItems.length} pending`}
                variant="yellow"
              />
            ) : !isLoading ? (
              <StatusBadge label="All clear" variant="green" />
            ) : null
          }
          minHeight={200}
        >
          <ReadinessQueueTable items={readinessItems} isLoading={isLoading} />
        </ChartCard>
      </section>

      {/* ── Section 5: Pending Onboarding (BOD-approved, not yet in training) ── */}
      <section>
        <ChartCard
          title="Pending Onboarding"
          subtitle="BOD-approved applicants not yet enrolled in recruit training — follow up to confirm onboarding is scheduled"
          headerRight={
            !applicantsLoading && !trainingLoading ? (
              <StatusBadge
                label={`${
                  allApplicants.filter((a) => {
                    const bodVote = asString(
                      (a as Record<string, unknown>)["bod_vote"] ??
                        (a as Record<string, unknown>)["BOD_Vote"] ??
                        "",
                    );
                    if (!bodVote) return false;
                    const stage = asString(a.pipeline_stage).toLowerCase();
                    const result = asString(
                      (a as Record<string, unknown>)["application_result"] ??
                        (a as Record<string, unknown>)["Application_Result"] ??
                        "",
                    );
                    const approved =
                      result.toLowerCase().includes("approved") ||
                      stage.includes("8") ||
                      stage.includes("complete");
                    if (!approved) return false;
                    const first = asString(a.first_name);
                    const last = asString(a.last_name);
                    const nameId = asString(a.applicant_name_id);
                    const fullName =
                      first && last ? `${first} ${last}`.trim() : nameId;
                    return fullName
                      ? !trainingNames.has(normalizeName(fullName))
                      : false;
                  }).length
                } awaiting`}
                variant="yellow"
              />
            ) : null
          }
          minHeight={200}
        >
          <PendingOnboardingTable
            applicants={allApplicants}
            trainingNames={trainingNames}
            isLoading={applicantsLoading || trainingLoading}
          />
        </ChartCard>
      </section>
    </div>
  );
}
